import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area, LineChart, Line,
} from 'recharts';
import { axios } from '../contexts/AuthContext';
import FilterPanel from '../components/FilterPanel';

const RADIAN = Math.PI / 180;
const renderPctLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.04) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  return (
    <text x={cx + r * Math.cos(-midAngle * RADIAN)} y={cy + r * Math.sin(-midAngle * RADIAN)}
      fill="white" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize:12, fontWeight:800, fontFamily:"'Outfit',sans-serif" }}>
      {`${(percent*100).toFixed(0)}%`}
    </text>
  );
};

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'rgba(255,255,255,0.97)', border:'1px solid rgba(255,107,0,0.15)', borderRadius:12, padding:'10px 14px', fontFamily:"'Outfit',sans-serif", boxShadow:'0 8px 24px rgba(0,0,0,0.12)' }}>
      <div style={{ fontWeight:700, fontSize:'0.82rem', color:'#5C3010', marginBottom:3 }}>{payload[0]?.name || label}</div>
      <div style={{ color:'#FF6B00', fontWeight:800, fontSize:'1.1rem' }}>{(payload[0]?.value||0).toLocaleString()}</div>
    </div>
  );
};

const Card = ({ title, children, loading, total, col=1 }) => (
  <div style={{ background:'white', borderRadius:20, padding:'20px', border:'1px solid rgba(255,107,0,0.1)', boxShadow:'0 4px 20px rgba(255,107,0,0.06)', transition:'all 0.3s', gridColumn:`span ${col}` }}
  onMouseEnter={e=>{e.currentTarget.style.boxShadow='0 12px 40px rgba(255,107,0,0.14)';e.currentTarget.style.transform='translateY(-3px)';}}
  onMouseLeave={e=>{e.currentTarget.style.boxShadow='0 4px 20px rgba(255,107,0,0.06)';e.currentTarget.style.transform='none';}}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
      <div style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:700, fontSize:'0.95rem', color:'#BF360C' }}>{title}</div>
      {total!==undefined && <div style={{ background:'rgba(255,107,0,0.08)', padding:'3px 10px', borderRadius:99, fontSize:'0.72rem', fontWeight:700, color:'#E55100' }}>{(total||0).toLocaleString()} voters</div>}
    </div>
    {loading
      ? <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:10 }}>
          <div style={{ width:36, height:36, border:'3px solid rgba(255,107,0,0.15)', borderTopColor:'#FF6B00', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
          <span style={{ fontSize:'0.75rem', color:'#C4A090' }}>Loading...</span>
        </div>
      : children
    }
  </div>
);

const NoData = () => (
  <div style={{ height:180, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, color:'#C4A090' }}>
    <span style={{ fontSize:'2.5rem' }}>📭</span>
    <span style={{ fontSize:'0.82rem' }}>No data available</span>
  </div>
);

export default function Analytics() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const isMobile = window.innerWidth < 900;
  const totalActive = Object.values(filters).flat().length;

  const fetchData = useCallback(async (f={}) => {
    setLoading(true);
    try {
      const params = {};
      if(f.districts?.length)   params.districts   = f.districts.join(',');
      if(f.communities?.length) params.communities = f.communities.join(',');
      if(f.genders?.length)     params.genders     = f.genders.join(',');
      if(f.age_groups?.length)  params.age_groups  = f.age_groups.join(',');
      if(f.professions?.length) params.professions = f.professions.join(',');
      const { data:res } = await axios.get('/stats/analytics', { params });
      setData(res);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData({}); }, []);
  const handleApply = () => { fetchData(filters); if(isMobile) setShowFilters(false); };
  const handleClear = () => { setFilters({}); fetchData({}); };

  const total     = data?.total || 0;
  const genderD   = (data?.genderDist||[]).map(d=>({ name:d.gender==='M'?'Male':d.gender==='F'?'Female':'Other', value:d.count }));
  const ageD      = (data?.ageDist||[]).map(d=>({ name:d.age_group, voters:d.count }));
  const distD     = (data?.districtDist||[]).map(d=>({ name:d.district||'Unknown', voters:d.count }));
  const commD     = (data?.communityDist||[]).filter(d=>d.community&&d.community!=='Unknown').slice(0,12).map(d=>({ name:d.community, voters:d.count }));
  const profD     = (data?.professionDist||[]).filter(d=>d.profession&&d.profession!=='Unknown').slice(0,10).map(d=>({ name:d.profession, voters:d.count }));
  const statusD   = (data?.statusDist||[]).map(d=>({ name:d.status||'None', value:d.count }));

  const GENDER_COLORS = ['#1565C0','#E91E63','#FF9800'];
  const STATUS_COLORS = { 'Available':'#00C853', 'Shifted':'#FFB300', 'Expired':'#E53935', 'None':'#90A4AE' };
  const COMM_COLORS   = ['#FF6B00','#1565C0','#2E7D32','#880E4F','#4A148C','#E65100','#00695C','#B71C1C','#F57F17','#37474F','#00838F','#558B2F'];
  const PROF_COLORS   = ['#1565C0','#0288D1','#0097A7','#00838F','#00695C','#2E7D32','#558B2F','#33691E','#F57F17','#E65100'];

  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(160deg,#FFF4ED,#FFF8F0)', fontFamily:"'Outfit',sans-serif" }}>

      {/* Header */}
      <div className="app-header">
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <button className="back-btn" onClick={()=>navigate('/')}>←</button>
          <div>
            <div style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:700, fontSize:'1.05rem', color:'white' }}>📊 Analytics Dashboard</div>
            {data && <div style={{ fontSize:'0.68rem', opacity:.85, color:'white' }}>{total.toLocaleString()} voters {totalActive>0&&`• ${totalActive} filters`}</div>}
          </div>
        </div>
        <button onClick={()=>setShowFilters(!showFilters)} style={{ background:totalActive>0?'rgba(255,255,255,0.35)':'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.4)', color:'white', padding:'7px 16px', borderRadius:99, cursor:'pointer', fontWeight:700, fontSize:'0.78rem', display:'flex', alignItems:'center', gap:6 }}>
          🎛️ Filters {totalActive>0&&<span style={{ background:'white', color:'#E55100', borderRadius:99, padding:'1px 7px', fontSize:'0.7rem', fontWeight:800 }}>{totalActive}</span>}
        </button>
      </div>

      {/* Mobile filter */}
      {isMobile && showFilters && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:300, display:'flex', flexDirection:'column', justifyContent:'flex-end' }} onClick={()=>setShowFilters(false)}>
          <div style={{ background:'white', borderRadius:'20px 20px 0 0', padding:16, maxHeight:'80vh', overflowY:'auto' }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
              <strong style={{ color:'#BF360C' }}>🎛️ Filters</strong>
              <button onClick={()=>setShowFilters(false)} style={{ background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer' }}>✕</button>
            </div>
            <FilterPanel filters={filters} onChange={setFilters} onApply={handleApply} onClear={handleClear}/>
          </div>
        </div>
      )}

      <div style={{ display:'flex', gap:20, padding:'20px', maxWidth:1400, margin:'0 auto', alignItems:'flex-start' }}>

        {/* CHARTS */}
        <div style={{ flex:1, minWidth:0 }}>

          {/* Summary */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:12, marginBottom:20 }}>
            {[
              { l:'Total Voters', v:total.toLocaleString(),                                                   img:'/images/total-voters.png', i:'👥', c:'#FF6B00', bg:'#FFF3E0' },
              { l:'Male',   v:(data?.genderDist?.find(g=>g.gender==='M')?.count||0).toLocaleString(),        img:'/images/male.png',         i:'👨', c:'#1565C0', bg:'#E3F2FD' },
              { l:'Female', v:(data?.genderDist?.find(g=>g.gender==='F')?.count||0).toLocaleString(),        img:'/images/female.png',       i:'👩', c:'#880E4F', bg:'#FCE4EC' },
              { l:'Districts', v:(data?.districtDist||[]).length,                img:'/images/districts.png',                              i:'🗺️', c:'#2E7D32', bg:'#E8F5E9' },
              { l:'Professions', v:(data?.professionDist||[]).filter(p=>p.profession!=='Unknown').length,  img:'/images/profession.png',   i:'💼', c:'#4A148C', bg:'#EDE7F6' },
            ].map(s=>(
              <div key={s.l} style={{ background:s.bg, borderRadius:14, padding:'14px 10px', textAlign:'center', boxShadow:'0 2px 10px rgba(0,0,0,0.06)', border:'1px solid rgba(255,255,255,0.8)', transition:'all 0.25s', cursor:'default' }}
              onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.12)';}}
              onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 2px 10px rgba(0,0,0,0.06)';}}>
                <div style={{ width:44, height:44, borderRadius:12, background:'rgba(255,255,255,0.7)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:6, overflow:'hidden' }}>
                  <img src={s.img} alt={s.l} style={{width:'82%',height:'82%',objectFit:'contain'}} onError={e=>{e.target.style.display='none';e.target.parentElement.innerHTML=`<span style="font-size:1.5rem">${s.i}</span>`;}}/>
                </div>
                <div style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:800, fontSize:'1.2rem', color:s.c }}>{s.v}</div>
                <div style={{ fontSize:'0.65rem', color:'#666', fontWeight:600 }}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Row 1: Gender Donut + Age Area */}
          <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:16, marginBottom:16 }}>
            <Card title="⚧️ Gender Distribution" total={total} loading={loading}>
              {genderD.length===0 ? <NoData/> : (
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie data={genderD} cx="45%" cy="50%" outerRadius={88} innerRadius={46} dataKey="value" labelLine={false} label={renderPctLabel} paddingAngle={4}>
                      {genderD.map((_,i)=><Cell key={i} fill={GENDER_COLORS[i]} stroke="white" strokeWidth={2}/>)}
                    </Pie>
                    <Tooltip content={<Tip/>}/>
                    <Legend iconType="circle" iconSize={9} layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize:'0.78rem', fontFamily:"'Outfit',sans-serif" }}/>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card title="🎂 Age Group Distribution" total={total} loading={loading}>
              {ageD.length===0 ? <NoData/> : (
                <ResponsiveContainer width="100%" height={230}>
                  <AreaChart data={ageD} margin={{ left:-10, right:10, top:10, bottom:0 }}>
                    <defs>
                      <linearGradient id="ageFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FF6B00" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#FF6B00" stopOpacity={0.02}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F5"/>
                    <XAxis dataKey="name" tick={{ fontSize:12, fontFamily:"'Outfit',sans-serif", fill:'#9E6040' }}/>
                    <YAxis tick={{ fontSize:10, fill:'#BDBDBD' }}/>
                    <Tooltip content={<Tip/>}/>
                    <Area type="monotone" dataKey="voters" name="Voters" stroke="#FF6B00" strokeWidth={3} fill="url(#ageFill)" dot={{ r:6, fill:'#FF6B00', stroke:'white', strokeWidth:2.5 }} activeDot={{ r:9, fill:'#E55100' }}/>
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {/* Row 2: District Horizontal + Status Donut */}
          <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'2fr 1fr', gap:16, marginBottom:16 }}>
            <Card title="🗺️ District-wise Voters" loading={loading}>
              {distD.length===0 ? <NoData/> : distD.length === 1 ? (
                /* Single district — show as a stat card */
                <div style={{ padding:'8px 0' }}>
                  {distD.map((d,i) => {
                    const colors = ['#FF6B00','#1565C0','#2E7D32','#880E4F','#4A148C','#E65100'];
                    const c = colors[i % colors.length];
                    return (
                      <div key={d.name} style={{ marginBottom:12 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div style={{ width:12, height:12, borderRadius:'50%', background:c }}/>
                            <span style={{ fontWeight:700, fontSize:'0.9rem', color:'#1A0A00' }}>{d.name}</span>
                          </div>
                          <span style={{ fontWeight:800, fontSize:'1.1rem', color:c }}>{d.voters.toLocaleString()}</span>
                        </div>
                        <div style={{ height:24, background:'#F5F5F5', borderRadius:12, overflow:'hidden' }}>
                          <div style={{ height:'100%', width:'100%', background:`linear-gradient(90deg, ${c}, ${c}88)`, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'flex-end', paddingRight:10 }}>
                            <span style={{ color:'white', fontSize:'0.72rem', fontWeight:700 }}>100%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ marginTop:16, padding:'12px', background:'rgba(255,107,0,0.05)', borderRadius:12, fontSize:'0.78rem', color:'#9E6040', textAlign:'center', lineHeight:1.6 }}>
                    💡 Import data from more districts to see district comparison chart
                  </div>
                </div>
              ) : (
                /* Multiple districts — horizontal bar chart */
                <ResponsiveContainer width="100%" height={Math.max(200, distD.length*52)}>
                  <BarChart data={distD} layout="vertical" margin={{ left:10, right:50, top:5, bottom:5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F5" horizontal={false}/>
                    <XAxis type="number" tick={{ fontSize:10, fill:'#BDBDBD' }}/>
                    <YAxis type="category" dataKey="name" tick={{ fontSize:12, fontFamily:"'Outfit',sans-serif", fill:'#5C3010' }} width={110}/>
                    <Tooltip content={<Tip/>}/>
                    <Bar dataKey="voters" name="Voters" radius={[0,8,8,0]} label={{ position:'right', fontSize:12, fontWeight:800, fill:'#333' }}>
                      {distD.map((_,i)=><Cell key={i} fill={['#FF6B00','#1565C0','#2E7D32','#880E4F','#4A148C','#E65100','#00695C','#B71C1C','#F57F17'][i%9]}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card title="📌 Voter Status" loading={loading}>
              {statusD.length===0 ? <NoData/> : (
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie data={statusD} cx="50%" cy="45%" outerRadius={78} innerRadius={36} dataKey="value" labelLine={false} label={renderPctLabel} paddingAngle={3}>
                      {statusD.map((d,i)=><Cell key={i} fill={STATUS_COLORS[d.name]||'#90A4AE'} stroke="white" strokeWidth={2}/>)}
                    </Pie>
                    <Tooltip content={<Tip/>}/>
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize:'0.72rem', fontFamily:"'Outfit',sans-serif" }}/>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {/* Row 3: Profession Bar (colorful) */}
          <Card title="💼 Profession-wise Distribution" loading={loading} col={isMobile?1:1}>
            {profD.length===0 ? (
              <div style={{ padding:'16px', textAlign:'center', color:'#C4A090', fontSize:'0.85rem' }}>
                📭 No profession data — will populate after voters update their profession
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={profD} margin={{ left:-10, right:10, top:5, bottom:70 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F5"/>
                  <XAxis dataKey="name" tick={{ fontSize:10, fontFamily:"'Outfit',sans-serif", fill:'#5C3010' }} angle={-35} textAnchor="end" interval={0}/>
                  <YAxis tick={{ fontSize:10, fill:'#BDBDBD' }}/>
                  <Tooltip content={<Tip/>}/>
                  <Bar dataKey="voters" name="Voters" radius={[8,8,0,0]}>
                    {profD.map((_,i)=><Cell key={i} fill={PROF_COLORS[i%PROF_COLORS.length]}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Row 4: Community */}
          <div style={{ marginTop:16 }}>
            <Card title="👥 Community Distribution" loading={loading}>
              {commD.length===0 ? (
                <div style={{ padding:'20px', textAlign:'center' }}>
                  <div style={{ fontSize:'1.5rem', marginBottom:8 }}>💡</div>
                  <div style={{ fontSize:'0.85rem', color:'#9E6040', marginBottom:6 }}>Community data not yet filled</div>
                  <div style={{ fontSize:'0.78rem', color:'#C4A090' }}>Update voter records with community info via Search Voter → Edit</div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={commD} margin={{ left:-10, right:10, top:5, bottom:80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F5"/>
                    <XAxis dataKey="name" tick={{ fontSize:9, fontFamily:"'Outfit',sans-serif", fill:'#5C3010' }} angle={-40} textAnchor="end" interval={0}/>
                    <YAxis tick={{ fontSize:10, fill:'#BDBDBD' }}/>
                    <Tooltip content={<Tip/>}/>
                    <Bar dataKey="voters" name="Voters" radius={[8,8,0,0]}>
                      {commD.map((_,i)=><Cell key={i} fill={COMM_COLORS[i%COMM_COLORS.length]}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {/* Row 5: Line Chart Overview */}
          {ageD.length>1&&(
            <div style={{ marginTop:16 }}>
              <Card title="📈 Age Distribution — Line View" loading={loading}>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={ageD} margin={{ left:0, right:20, top:10, bottom:10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F5"/>
                    <XAxis dataKey="name" tick={{ fontSize:12, fontFamily:"'Outfit',sans-serif", fill:'#9E6040' }}/>
                    <YAxis tick={{ fontSize:10, fill:'#BDBDBD' }}/>
                    <Tooltip content={<Tip/>}/>
                    <Line type="monotone" dataKey="voters" name="Voters" stroke="#FF6B00" strokeWidth={3}
                      dot={{ r:7, fill:'#FF6B00', stroke:'white', strokeWidth:3 }}
                      activeDot={{ r:10, fill:'#E55100', stroke:'white', strokeWidth:2 }}/>
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </div>
          )}
        </div>

        {/* RIGHT SIDEBAR */}
        {!isMobile && (
          <div style={{ width:260, flexShrink:0, position:'sticky', top:20 }}>
            <FilterPanel filters={filters} onChange={setFilters} onApply={handleApply} onClear={handleClear}/>
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}